import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, TablePagination, IconButton, TableSortLabel } from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Address } from "@/types/address.types";
import { formatDate } from "@/utils/date.utils";
import { useState, useMemo } from "react";

interface AddressTableProps {
  addresses: Address[];
  loading?: boolean;
  onEdit: (address: Address) => void;
  onDelete: (id: string) => void;
}

export default function AddressTable({
  addresses,
  loading,
  onEdit,
  onDelete,
}: AddressTableProps): JSX.Element {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof Address>('createdAt');

  const handleSortRequest = (property: keyof Address): void => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // 排序逻辑
  const sortedAddresses = useMemo(() => {
    const comparator = (a: Address, b: Address): number => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      // 处理 undefined 值
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return order === 'asc' ? -1 : 1;
      if (bValue === undefined) return order === 'asc' ? 1 : -1;
      
      // 比较值
      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    };
    return [...addresses].sort(comparator);
  }, [addresses, order, orderBy]);

  if (loading) {
    return <Box>加载中...</Box>;
  }

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const paginatedAddresses = sortedAddresses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'call'}
                  direction={orderBy === 'call' ? order : 'asc'}
                  onClick={() => handleSortRequest('call')}
                >
                  呼号
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ maxWidth: 400 }}>
                <TableSortLabel
                  active={orderBy === 'address'}
                  direction={orderBy === 'address' ? order : 'asc'}
                  onClick={() => handleSortRequest('address')}
                >
                  地址
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'zip'}
                  direction={orderBy === 'zip' ? order : 'asc'}
                  onClick={() => handleSortRequest('zip')}
                >
                  邮编
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSortRequest('status')}
                >
                  状态
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleSortRequest('createdAt')}
                >
                  创建时间
                </TableSortLabel>
              </TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAddresses.map((address: Address) => (
              <TableRow key={address.id}>
                <TableCell>{address.call}</TableCell>
                <TableCell sx={{
                  maxWidth: 400,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }}>
                  {address.address}
                </TableCell>
                <TableCell>{address.zip}</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: address.status === 'valid' ? '#2e7d32' : '#c62828',
                      backgroundColor: address.status === 'valid' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(198, 40, 40, 0.1)',
                    }}
                  >
                    {address.status === "valid" ? "有效" : "无效"}
                  </Box>
                </TableCell>
                <TableCell>{formatDate(address.createdAt)}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onEdit(address)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      },
                      mr: 0.5,
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(address.id)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={addresses.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}
